/** Query contract for Communicate GET /ctl-preview (clinio-communicate PR #59).
 *
 *  Params: limit (default 100, clamp 100; invalid → 400), offset (default 0),
 *  search (case-insensitive substring of envelope_identifier OR letter_identifier
 *  only), kind (exact TypeCode; several as comma-separated or repeated kind=;
 *  slash-separated is NOT a list).
 *  Order: created_at DESC, id DESC.
 *  Body: { ok, data: { consumerName, exchanges, total, hasMore, limit, offset } }.
 *  Paging is top-level on data (no pagination object). The list field is
 *  exchanges, not items. Old servers omit hasMore/total — walk with
 *  empty/short/repeat/cap. */

export const CTL_PREVIEW_DEFAULT_LIMIT = 100;
export const CTL_PREVIEW_MAX_LIMIT = 100;
export const CTL_PREVIEW_SEARCH_MAX = 64;
/** Comma lists such as XBIN01,XBIN02 or XDIS91,XBIN01,XREF01,XREF02,XREF06. */
export const CTL_PREVIEW_KIND_MAX = 64;

export const CORRESPONDENCE_DEFAULT_LIMIT = 20;
export const CORRESPONDENCE_MAX_LIMIT = 50;
/** Default Modtagne korrespondancer view: XDIS91s only, not the mixed log. */
export const CORRESPONDENCE_DEFAULT_KIND = 'XDIS91';

export const CTL_PREVIEW_MAX_PAGES = 10;
export const CTL_PREVIEW_XBIN_KINDS = 'XBIN01,XBIN02';

export type CtlPreviewStop = 'empty' | 'short' | 'repeat' | 'cap' | 'done' | 'next';

export type CtlPreviewQueryInput = {
  limit?: unknown;
  offset?: unknown;
  search?: unknown;
  kind?: unknown;
  type?: unknown;
};

export type CtlPreviewQuery = {
  limit: number;
  offset: number;
  search: string | undefined;
  kind: string | undefined;
};

export type ParseCtlPreviewQueryResult =
  | { ok: true; query: CtlPreviewQuery }
  | { ok: false; field: 'limit' };

function toExactInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  return undefined;
}

function cleanText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, max);
  return trimmed.length > 0 ? trimmed : undefined;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Exact TypeCodes. Comma (or a string[]) is a list; a slash is part of the value. */
export function parseCtlPreviewKindParam(value: unknown): string | undefined {
  const parts: string[] = [];
  const push = (item: unknown) => {
    if (typeof item !== 'string') return;
    for (const piece of item.split(',')) {
      const trimmed = piece.trim();
      if (trimmed.length > 0) parts.push(trimmed);
    }
  };
  if (Array.isArray(value)) {
    for (const item of value) push(item);
  } else {
    push(value);
  }
  if (parts.length === 0) return undefined;
  return parts.join(',').slice(0, CTL_PREVIEW_KIND_MAX);
}

export function ctlPreviewKindList(kind: string | undefined): string[] {
  if (!kind) return [];
  return kind.split(',').map((item) => item.trim()).filter((item) => item.length > 0);
}

export function sanitizeCtlPreviewQuery(
  input: CtlPreviewQueryInput | undefined,
  defaults?: { limit?: number; maxLimit?: number },
): CtlPreviewQuery {
  const maxLimit = defaults?.maxLimit ?? CTL_PREVIEW_MAX_LIMIT;
  const fallbackLimit = defaults?.limit ?? CTL_PREVIEW_DEFAULT_LIMIT;
  const limitRaw = input?.limit === undefined || input.limit === null || input.limit === ''
    ? fallbackLimit
    : (toExactInt(input.limit) ?? fallbackLimit);
  const offsetRaw = toExactInt(input?.offset);
  return {
    limit: clamp(limitRaw < 1 ? fallbackLimit : limitRaw, 1, maxLimit),
    offset: Math.max(0, offsetRaw ?? 0),
    search: cleanText(input?.search, CTL_PREVIEW_SEARCH_MAX),
    kind: parseCtlPreviewKindParam(input?.kind ?? input?.type),
  };
}

/** Invalid limit → 400 invalid-input (Communicate PR #59). Missing/oversize is clamped. */
export function parseCtlPreviewQuery(
  input: CtlPreviewQueryInput | undefined,
  defaults?: { limit?: number; maxLimit?: number },
): ParseCtlPreviewQueryResult {
  if (input && input.limit !== undefined && input.limit !== null && input.limit !== '') {
    const exact = toExactInt(input.limit);
    if (exact === undefined || exact < 1) return { ok: false, field: 'limit' };
  }
  return { ok: true, query: sanitizeCtlPreviewQuery(input, defaults) };
}

export function sanitizeCorrespondenceListQuery(input: CtlPreviewQueryInput | undefined): CtlPreviewQuery {
  const query = sanitizeCtlPreviewQuery(input, {
    limit: CORRESPONDENCE_DEFAULT_LIMIT,
    maxLimit: CORRESPONDENCE_MAX_LIMIT,
  });
  return { ...query, kind: query.kind ?? CORRESPONDENCE_DEFAULT_KIND };
}

export function parseCorrespondenceListQuery(input: CtlPreviewQueryInput | undefined): ParseCtlPreviewQueryResult {
  const parsed = parseCtlPreviewQuery(input, {
    limit: CORRESPONDENCE_DEFAULT_LIMIT,
    maxLimit: CORRESPONDENCE_MAX_LIMIT,
  });
  if (!parsed.ok) return parsed;
  return { ok: true, query: { ...parsed.query, kind: parsed.query.kind ?? CORRESPONDENCE_DEFAULT_KIND } };
}

export type CtlExchangeSearchRow = {
  id?: unknown;
  kind?: unknown;
  standard?: unknown;
  envelopeIdentifier?: unknown;
  letterIdentifier?: unknown;
  originalEnvelopeIdentifier?: unknown;
  originalLetterIdentifier?: unknown;
};

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function exchangeMatchesCtlPreviewQuery(row: CtlExchangeSearchRow, query: CtlPreviewQuery): boolean {
  const kinds = ctlPreviewKindList(query.kind);
  if (kinds.length > 0) {
    const kind = asText(row.kind) || asText(row.standard);
    if (!kinds.includes(kind)) return false;
  }
  if (query.search) {
    const needle = query.search.toLowerCase();
    const envelope = asText(row.envelopeIdentifier).toLowerCase();
    const letter = asText(row.letterIdentifier).toLowerCase();
    if (!envelope.includes(needle) && !letter.includes(needle)) return false;
  }
  return true;
}

export function filterCtlExchanges<T extends CtlExchangeSearchRow>(rows: T[], query: CtlPreviewQuery): T[] {
  return rows.filter((row) => exchangeMatchesCtlPreviewQuery(row, query));
}

export function capCtlPreviewPage<T>(rows: T[], limit: number): T[] {
  return rows.slice(0, Math.min(Math.max(1, limit), CTL_PREVIEW_MAX_LIMIT));
}

export function ctlPreviewHasMore(returnedCount: number, limit: number): boolean {
  return returnedCount >= limit;
}

/** Wire params for GET /ctl-preview. No `type` alias; kind may be a comma list. */
export function ctlPreviewQueryParams(query: CtlPreviewQuery): Record<string, string | number | undefined> {
  return {
    limit: query.limit,
    offset: query.offset,
    search: query.search,
    kind: query.kind,
  };
}

export type CtlPreviewListData = {
  consumerName: string;
  exchanges: unknown[];
  total: number | undefined;
  hasMore: boolean | undefined;
  limit: number | undefined;
  offset: number | undefined;
};

function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : undefined;
}

/** Read PR #59 `data`. Uses `exchanges` (not `items`); paging is top-level (no `pagination`). */
export function readCtlPreviewListData(data: Record<string, unknown>): CtlPreviewListData {
  const exchanges = Array.isArray(data.exchanges) ? data.exchanges : [];
  return {
    consumerName: typeof data.consumerName === 'string' ? data.consumerName : '',
    exchanges,
    total: optionalInt(data.total),
    hasMore: typeof data.hasMore === 'boolean' ? data.hasMore : undefined,
    limit: optionalInt(data.limit),
    offset: optionalInt(data.offset),
  };
}

export function ctlPreviewHasServerPaging(meta: { hasMore?: boolean; total?: number }): boolean {
  return typeof meta.hasMore === 'boolean' || typeof meta.total === 'number';
}

export function ctlPreviewHasMoreFromServer(meta: {
  hasMore?: boolean;
  total?: number;
  offset: number;
  limit: number;
  returnedCount: number;
}): boolean {
  if (typeof meta.hasMore === 'boolean') return meta.hasMore;
  if (typeof meta.total === 'number') return meta.offset + meta.returnedCount < meta.total;
  return ctlPreviewHasMore(meta.returnedCount, meta.limit);
}

export function pageRowIds(rows: readonly { id?: unknown }[]): string[] {
  return rows
    .map((row) => (typeof row.id === 'string' ? row.id : ''))
    .filter((id) => id.length > 0);
}

/** True when Communicate ignored offset and sent the same newest-100 again. */
export function ctlPreviewPagesRepeat(
  currentIds: readonly string[],
  previousIds: readonly string[] | null,
): boolean {
  if (!previousIds || previousIds.length === 0 || currentIds.length === 0) return false;
  if (currentIds.length !== previousIds.length) return false;
  return currentIds.every((id, index) => id === previousIds[index]);
}

export function decideCtlPreviewAdvance(input: {
  pageIds: readonly string[];
  previousPageIds: readonly string[] | null;
  requestedLimit: number;
  pageCount: number;
  maxPages: number;
}): { continue: boolean; reason: CtlPreviewStop } {
  if (input.pageIds.length === 0) return { continue: false, reason: 'empty' };
  if (ctlPreviewPagesRepeat(input.pageIds, input.previousPageIds)) {
    return { continue: false, reason: 'repeat' };
  }
  if (input.pageIds.length < input.requestedLimit) return { continue: false, reason: 'short' };
  if (input.pageCount >= input.maxPages) return { continue: false, reason: 'cap' };
  return { continue: true, reason: 'next' };
}

export type CtlPreviewFetchedPage<T> = {
  rows: T[];
  hasMore?: boolean;
  total?: number;
  consumerName?: string;
};

export async function walkCtlPreviewToOffset<T extends { id?: unknown }>(
  fetchPage: (offset: number) => Promise<T[]>,
  query: { limit: number; offset: number },
  maxPages = CTL_PREVIEW_MAX_PAGES,
): Promise<{
  rows: T[];
  firstPage: T[];
  hasMore: boolean;
  stopped: CtlPreviewStop;
  ignoredOffset: boolean;
}> {
  let previousIds: string[] | null = null;
  let offset = 0;
  let pageCount = 0;
  let firstPage: T[] = [];
  while (offset <= query.offset) {
    const page = await fetchPage(offset);
    pageCount += 1;
    if (offset === 0) firstPage = page;
    const ids = pageRowIds(page);
    const decision = decideCtlPreviewAdvance({
      pageIds: ids,
      previousPageIds: previousIds,
      requestedLimit: query.limit,
      pageCount,
      maxPages,
    });
    if (decision.reason === 'repeat') {
      return {
        rows: query.offset === 0 ? page : [],
        firstPage,
        hasMore: false,
        stopped: 'repeat',
        ignoredOffset: true,
      };
    }
    if (decision.reason === 'empty') {
      return { rows: [], firstPage, hasMore: false, stopped: 'empty', ignoredOffset: false };
    }
    if (offset === query.offset) {
      return {
        rows: page,
        firstPage,
        hasMore: decision.continue,
        stopped: decision.reason,
        ignoredOffset: false,
      };
    }
    if (!decision.continue) {
      return { rows: [], firstPage, hasMore: false, stopped: decision.reason, ignoredOffset: false };
    }
    previousIds = ids;
    offset += query.limit;
  }
  return { rows: [], firstPage, hasMore: false, stopped: 'done', ignoredOffset: false };
}

/** Prefer hasMore/total from Communicate PR #59; else empty/short/repeat/cap. */
export async function loadCtlPreviewPage<T extends { id?: unknown }>(
  fetchPage: (offset: number) => Promise<CtlPreviewFetchedPage<T>>,
  query: { limit: number; offset: number },
  maxPages = CTL_PREVIEW_MAX_PAGES,
): Promise<{
  rows: T[];
  firstPage: T[];
  hasMore: boolean;
  total: number | undefined;
  stopped: CtlPreviewStop;
  ignoredOffset: boolean;
  usedServerPaging: boolean;
  consumerName: string;
}> {
  const probed = await fetchPage(query.offset);
  const consumerName = probed.consumerName ?? '';
  if (ctlPreviewHasServerPaging(probed)) {
    return {
      rows: probed.rows,
      firstPage: probed.rows,
      hasMore: ctlPreviewHasMoreFromServer({
        hasMore: probed.hasMore,
        total: probed.total,
        offset: query.offset,
        limit: query.limit,
        returnedCount: probed.rows.length,
      }),
      total: probed.total,
      stopped: 'done',
      ignoredOffset: false,
      usedServerPaging: true,
      consumerName,
    };
  }
  if (query.offset === 0) {
    const decision = decideCtlPreviewAdvance({
      pageIds: pageRowIds(probed.rows),
      previousPageIds: null,
      requestedLimit: query.limit,
      pageCount: 1,
      maxPages,
    });
    return {
      rows: probed.rows,
      firstPage: probed.rows,
      hasMore: decision.continue,
      total: undefined,
      stopped: decision.reason,
      ignoredOffset: false,
      usedServerPaging: false,
      consumerName,
    };
  }
  const walked = await walkCtlPreviewToOffset(
    async (offset) => (await fetchPage(offset)).rows,
    query,
    maxPages,
  );
  return {
    ...walked,
    total: undefined,
    usedServerPaging: false,
    consumerName,
  };
}
